import React, { useState } from 'react';
import { ChevronDown, ArrowRight } from 'lucide-react';

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-gray-200">
      <button
        className="w-full py-6 flex justify-between items-center text-left"
        onClick={onClick}
      >
        <h3 className="text-lg font-semibold text-[#113262] pr-8">{question}</h3>
        <ChevronDown 
          className={`w-5 h-5 text-[#F49611] transform transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96 pb-6' : 'max-h-0'
        }`}
      >
        <p className="text-gray-600">{answer}</p>
      </div>
    </div>
  );
};

const ThreeLensFrameworkFaqs = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      question: "What is the Three-Lens Framework?",
      answer: "The Three-Lens Framework is our proprietary research model that screens and selects high-quality mutual funds. It performs three critical checks before recommending any fund: Equity Portfolio Quality (assessing the financial strength of companies in the fund), Bond Credit Quality (evaluating the security of debt holdings), and Peer Group Ranking (analyzing how the fund performs relative to others in its category). This comprehensive approach helps us identify funds with the highest probability of delivering stable, long-term results."
    },
    {
      question: "How does the Equity Portfolio Quality lens work?",
      answer: "The Equity Portfolio Quality lens examines the underlying companies held in equity mutual funds. We analyze balance sheets and business fundamentals to ensure funds are investing in financially strong, well-managed companies. We specifically look for companies with low debt and stable balance sheets, high Return on Equity (ROE) aligned with shareholder interests, and strong promoter ownership and corporate governance. We avoid funds with high exposure to risky companies, stocks with weak profitability and high leverage, and those holding turnaround stories with poor earnings visibility."
    },
    {
      question: "What does the Bond Credit Quality lens evaluate?",
      answer: "The Bond Credit Quality lens focuses on the safety of debt holdings in hybrid or debt-oriented funds. We examine the credit quality of bonds held by mutual funds, prioritizing those with high-grade, less fragile debt structures. We specifically look for funds holding AAA-rated sovereign and corporate bonds, low-default risk instruments, and those that avoid structured credit or promoter-backed risky debt. This lens ensures that the 'safety' portion of your mutual fund portfolio is genuinely secure, not just marketed that way."
    },
    {
      question: "How does the Peer Group Ranking lens compare funds?",
      answer: "The Peer Group Ranking lens measures how a fund performs relative to others in its category using sophisticated analysis techniques. Rather than looking at returns in isolation, we use quartile-based peer ranking, rolling return analysis (over 3, 5, and 7 years), and monthly rank movement tracking. We identify red flags such as funds consistently ranked in bottom quartiles, those failing to beat benchmarks over medium to long-term periods, and funds with high volatility without commensurate returns. This helps us avoid underperformers regardless of their popularity."
    },
    {
      question: "What additional filters do you apply beyond the Three-Lens Framework?",
      answer: "Beyond our core Three-Lens Framework, we apply additional quality filters to ensure comprehensive evaluation. We assess AUM stability, avoiding funds that are too small or face frequent outflows which can impact performance. We evaluate fund manager tenure and track record, recognizing that experience matters significantly in fund management. We also consider portfolio churn rate, preferring funds with lower turnover to reduce cost drag. These additional filters help us deliver mutual fund recommendations that complement your overall wealth management strategy."
    },
    {
      question: "Why is a framework-based approach better than chasing past returns?",
      answer: "A framework-based approach like our Three-Lens Framework removes emotion from investing decisions and provides consistency. Chasing past returns often leads to disappointment as yesterday's winners frequently become tomorrow's underperformers. Our process-based methodology evaluates the fundamental quality of funds and their holdings rather than recent performance, which can be influenced by market trends or luck. As Warren Buffett wisely noted, 'Risk comes from not knowing what you're doing.' Our disciplined framework ensures you're invested in high-quality funds aligned with your risk profile and financial goals, not just recent performers."
    }
  ];

  return (
    <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
      <div className="flex flex-col md:flex-row gap-16">
        <div className="md:w-1/3">
          <div className="sticky top-8">
            <div className="w-12 h-0.5 bg-[#F49611] mb-3"></div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4">
              Understanding the Three-Lens Framework
            </h2>
            <p className="text-gray-600 mb-8">
              Learn more about our proprietary research model that helps us identify high-quality mutual funds with the greatest potential for stable, long-term performance.
            </p>
            <a href="/meeting" className="flex items-center text-[#F49611] font-semibold hover:gap-2 transition-all">
              <span>Speak With An Expert</span>
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
        <div className="md:w-2/3">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={index === openIndex}
              onClick={() => setOpenIndex(index === openIndex ? -1 : index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ThreeLensFrameworkFaqs;